import React, { useCallback } from 'react';
import classnames from 'classnames';
import chunk from 'lodash/chunk';

import { ReactComponent as ChevronDownSVG } from '../../../assets/icons/chevron-down.svg';
import AutoWidthInput from '../controls/auto-width-input/AutoWidthInput';

import './SpeedLimit.scss';

export enum SPEED_UNITS {
  milesPerHour = 'mph',
  kilometersPerHour = 'kph',
}

const speedUnitOptions = {
  [SPEED_UNITS.milesPerHour]: {
    label: 'MPH',
    value: SPEED_UNITS.milesPerHour,
    maxSpeed: 155,
  },
  [SPEED_UNITS.kilometersPerHour]: {
    label: 'KPH',
    value: SPEED_UNITS.kilometersPerHour,
    maxSpeed: 250,
  },
};

const speedUnitOptionsList = [
  speedUnitOptions[SPEED_UNITS.milesPerHour],
  speedUnitOptions[SPEED_UNITS.kilometersPerHour],
];

const speedLimitPresets = {
  [SPEED_UNITS.milesPerHour]: [
    5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75,
  ],
  [SPEED_UNITS.kilometersPerHour]: [
    10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150,
  ],
};

// should be changed in css as well
const PRESET_ROW_ELEMENTS_COUNT = 6;

export interface SpeedLimitProps {
  className?: string;
  disabled?: boolean;
  value?;
  hasError?;
  onChange?;
}

const SpeedLimit = ({
  className,
  disabled,
  value = { unit: speedUnitOptions[SPEED_UNITS.milesPerHour], value: null },
  hasError,
  onChange,
}: SpeedLimitProps) => {
  const { unit: selectedUnit = SPEED_UNITS.milesPerHour, value: speed } = value;

  const handleInputChange = useCallback(
    ({ target: { value: newSpeed, validity: { valid = false } = {} } }) => {
      let value;
      const maxSpeed = Object.values(speedUnitOptions).find(
        (unit) => unit.value === selectedUnit,
      )?.maxSpeed;

      if (valid) {
        value = newSpeed ? parseFloat(newSpeed) : null;

        if (value) {
          if (value <= 0) {
            value = null;
          } else if (maxSpeed && value > maxSpeed) {
            value = maxSpeed;
          }
        }
      } else {
        value = speed;
      }

      onChange({
        unit: selectedUnit,
        value,
      });
    },
    [onChange, selectedUnit, speed],
  );

  const handlePresetButtonClick = useCallback(
    (speed) => onChange({ unit: selectedUnit, value: speed }),
    [onChange, selectedUnit],
  );

  const handleUnitsSelectChange = useCallback(
    ({ target: { value: unit } }) => {
      onChange({ value: speed, unit });
    },
    [onChange, speed],
  );

  return (
    <div className={classnames('speed-limit', className)}>
      <div className="speed-limit__units-select-container">
        <select
          className="speed-limit__units-select"
          disabled={disabled}
          value={selectedUnit}
          onChange={handleUnitsSelectChange}
        >
          {speedUnitOptionsList.map((unit) => (
            <option
              key={unit.value}
              className="speed-limit__unit-option"
              value={unit.value}
            >
              {unit.label}
            </option>
          ))}
        </select>
        <ChevronDownSVG className="speed-limit__units-select-chevron" />
      </div>
      <AutoWidthInput
        className="speed-limit__speed-input"
        // type="text" is required for pattern validation
        type="text"
        placeholder="Max. speed"
        disabled={disabled}
        pattern="\d*"
        hasError={hasError}
        value={speed || ''}
        onChange={handleInputChange}
      />

      <div className="speed-limit__preset-buttons">
        {chunk(speedLimitPresets[selectedUnit], PRESET_ROW_ELEMENTS_COUNT).map(
          (presets, chunkIdx) => (
            <div key={chunkIdx} className="speed-limit__preset-buttons-row">
              {presets.map((limit) => (
                <button
                  key={String(limit)}
                  className={classnames('speed-limit__preset-button', {
                    'speed-limit__preset-button_active': limit === speed,
                  })}
                  type="button"
                  disabled={disabled}
                  onClick={() => handlePresetButtonClick(limit)}
                >
                  {limit}
                </button>
              ))}
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default SpeedLimit;
